import React, { useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  TextField,
  Chip,
  useTheme,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import { Autocomplete } from "@material-ui/lab";
import { CustomTheme } from "../../../../../../types/customTheme";

interface TagsSpotlightSectionProps {
  currentMenuData: any;
  selectedTags: any[];
  onTagsChange: (tags: any[]) => void;
  spotlightText: string;
  onSpotlightTextChange: (text: string) => void;
  classes: any;
  wrapperData?: any;
}

const TagsSpotlightSection: React.FC<TagsSpotlightSectionProps> = ({
  currentMenuData,
  selectedTags,
  onTagsChange,
  spotlightText,
  onSpotlightTextChange,
  classes,
  wrapperData,
}) => {
  const theme: CustomTheme = useTheme();

  // Get all available tags from currentMenuData
  const availableTags = currentMenuData?.tags
    ? Object.values(currentMenuData.tags)
    : [];

  // Helper function to get tag objects from tag IDs
  const getTagObjectsFromIds = (tagIds: string[]) => {
    if (!tagIds || !availableTags.length) return [];
    return tagIds
      .map((id) => availableTags.find((tag: any) => tag.id === id))
      .filter((tag) => tag !== undefined);
  };

  // Initialize selectedTags from wrapperData.tags when component mounts or wrapperData changes
  useEffect(() => {
    if (
      wrapperData?.tags &&
      wrapperData.tags.length > 0 &&
      availableTags.length > 0
    ) {
      const initialTags = getTagObjectsFromIds(wrapperData.tags);
      if (
        initialTags.length > 0 &&
        JSON.stringify(initialTags) !== JSON.stringify(selectedTags)
      ) {
        onTagsChange(initialTags);
      }
    } else if (
      wrapperData?.tags &&
      wrapperData.tags.length === 0 &&
      selectedTags.length > 0
    ) {
      // Clear tags if wrapper has no tags
      onTagsChange([]);
    }
  }, [wrapperData?.tags, availableTags]);

  const handleTagsChange = (event: any, newValue: any[]) => {
    onTagsChange(newValue);
  };

  const handleDeleteTag = (index: number) => {
    const newTags = selectedTags.filter((_, i) => i !== index);
    onTagsChange(newTags);
  };

  return (
    <Paper
      elevation={0}
      style={{
        marginBottom: 24,
        borderRadius: 16,
        backgroundColor: theme.palette.background.menuProductColor,
        padding: 24,
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Box display="flex" alignItems="center" mb={4}>
        <Box
          style={{
            backgroundColor: "#5e35b1",
            borderRadius: 8,
            padding: 6,
            marginRight: 12,
          }}
        >
          <Icon icon="lucide:tag" width="20" style={{ color: "white" }} />
        </Box>
        <Box>
          <Typography variant="h6" style={{ fontWeight: "bold" }} align="left">
            Tags & Spotlight
          </Typography>
          <Typography variant="caption" align="left">
            Organise and highlight this wrapper.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Tags Column */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="body2"
            style={{ marginBottom: 8, fontWeight: 500 }}
            align="left"
          >
            Tags
          </Typography>

          {/* Autocomplete Input */}
          <Autocomplete
            multiple
            size="small"
            options={availableTags}
            getOptionLabel={(option: any) => option?.name || ""}
            value={selectedTags}
            onChange={handleTagsChange}
            classes={{ root: classes.autoComplete }}
            renderTags={() => null}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select tags"
                className={classes.textField}
              />
            )}
          />

          {/* Selected Tags rendered OUTSIDE Autocomplete */}
          <Box display="flex" flexWrap="wrap" style={{ marginTop: "8px" }}>
            {selectedTags.map((option: any, index: number) => (
              <Chip
                key={option?.id || index}
                label={option?.name || ""}
                className={classes.tagChip}
                onDelete={() => handleDeleteTag(index)}
                deleteIcon={<Icon icon="lucide:x" width="14" />}
              />
            ))}
          </Box>
        </Grid>

        {/* Spotlight Column - Changed to single line input */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="body2"
            style={{ marginBottom: 8, fontWeight: 500 }}
            align="left"
          >
            Spotlight
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Best seller!"
            className={classes.textField}
            value={spotlightText}
            onChange={(e) => onSpotlightTextChange(e.target.value)}
            InputProps={{
              style: {},
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TagsSpotlightSection;
